import Button from "components/Elements/Button/Button";
import Icon from "components/Elements/Icon/Icon";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement";
import ReCAPTCHA from "react-google-recaptcha";
import useMfa from "hooks/useMfa";
import dayjs from "dayjs";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha";

import { useEffect, useRef, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useUser } from "contexts/userContext/userContext";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";
import { awaiter } from "../../../../../Home/Helpers/helpers";

import styles from "./manageMfaModal.module.scss";

interface ManageMfaModalI {
  enableMfa: boolean;
  onMFACodeCheck: (...props: unknown[]) => undefined | string;
  skipFirstStep?: boolean;
  email?: string;
  isSubUsersManagement?: boolean;
}

type CaptchaCallbackNameT = "resend" | "check" | "";

const ONE_HOUR_MS = 3600000;

const sessionId =
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15);

const ManageMfaModal = (props: ManageMfaModalI) => {
  const {
    enableMfa,
    onMFACodeCheck,
    skipFirstStep,
    isSubUsersManagement,
    email,
  } = props;

  const { getBasicSettingsData, getTextByKey } = useContent();
  const { closeModal } = useModal();
  const { getUserData } = useUser();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [token, setToken] = useState("");
  const [captchaCallbackName, setCaptchaCallbackName] =
    useState<CaptchaCallbackNameT>("");

  const {
    sendMfaCodeOnEmail,
    code,
    setCode,
    resendTimeLeft,
    isResendEnabled,
    isLoading,
    startTimer,
    isMailSent,
    toggleMfa,
  } = useMfa();

  const [isCallbackExecuting, setIsCallbackExecuting] = useState(false);
  const [error, setError] = useState("");

  const codeExpirationTimer = useRef<NodeJS.Timeout>();
  const [timeLeft, setTimeLeft] = useState(ONE_HOUR_MS);

  useEffect(() => {
    if (isMailSent || skipFirstStep) {
      setTimeLeft(ONE_HOUR_MS);

      codeExpirationTimer.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1000);
      }, 1000);
    } else {
      clearInterval(codeExpirationTimer.current as NodeJS.Timeout);
    }
  }, [isMailSent, skipFirstStep]);

  const handleSetCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) {
      setError("");
    }

    const valueNumbersOnly = e.target.value.trim().replace(/[^0-9]/g, "");
    const value = valueNumbersOnly.slice(0, 6);

    setCode(value);
    setToken("");
    setShowRecaptcha(false);
  };

  const handleExecuteCallback = async () => {
    setIsCallbackExecuting(true);
    if (onMFACodeCheck) {
      if (!token) {
        setCaptchaCallbackName("check");
        setShowRecaptcha(true);
        if (getBasicSettingsData().recaptchaType === "google") {
          await awaiter(RECAPTCHA_APPEAR_DELAY);
          await recaptchaRef.current?.executeAsync();
        }
        setIsCallbackExecuting(false);
        return;
      }

      try {
        const error = await onMFACodeCheck(code, enableMfa, sessionId, token);

        if (error) {
          setError(error);
          setToken("");
          setShowRecaptcha(false);
        }
      } catch (err) {
        setError(
          getTextByKey("something-went-wrong") || "Something went wrong"
        );
        setToken("");
        setShowRecaptcha(false);
      }
    } else {
      try {
        const error = await toggleMfa(code, enableMfa, sessionId);

        if (error) {
          setError(error);
          setToken("");
          setShowRecaptcha(false);
        }
      } catch (err) {
        setError(
          getTextByKey("something-went-wrong") || "Something went wrong"
        );
        setToken("");
        setShowRecaptcha(false);
      }
    }

    startTimer();
    setIsCallbackExecuting(false);
  };

  const handleResendMfaCode = async () => {
    if (!token) {
      setCaptchaCallbackName("resend");
      setShowRecaptcha(true);
      if (getBasicSettingsData().recaptchaType === "google") {
        await awaiter(RECAPTCHA_APPEAR_DELAY);
        await recaptchaRef.current?.executeAsync();
      }
      return;
    }

    const userEmail = email || getUserData()?.UserName;
    if (userEmail) {
      sendMfaCodeOnEmail(token, sessionId, userEmail);
      setTimeLeft(ONE_HOUR_MS);
    } else {
      console.error("Email is not provided");
    }
  };

  useEffect(() => {
    if (token) {
      if (captchaCallbackName === "check") {
        handleExecuteCallback();
      } else if (captchaCallbackName === "resend") {
        handleResendMfaCode();
      }

      setShowRecaptcha(false);
      setToken("");
    }
  }, [token, captchaCallbackName]);

  useEffect(() => {
    startTimer();

    return () => {
      // Cleanup on component unmount
      clearInterval(codeExpirationTimer.current);
      setIsCallbackExecuting(false);
      setShowRecaptcha(false);
      setToken("");
      setError("");
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      <Icon
        name="close"
        onClick={closeModal}
        white
        medium
        iconClass={styles.closeIcon}
      />
      {showRecaptcha ? (
        <>
          <Recaptcha
            recaptchaRef={recaptchaRef}
            size="invisible"
            onSuccess={(token: string) => setToken(token)}
          />
        </>
      ) : null}
      {isMailSent || skipFirstStep ? (
        <>
          <h2>{getTextByKey("check-your-email-mfa-title")}</h2>
          <p>
            {getTextByKey("check-your-email-mfa-description").replace(
              "#email#",
              getTextByKey(getUserData()?.UserName || "")
            )}
          </p>
          <input
            value={code}
            onChange={handleSetCode}
            placeholder="* * * * * *"
          />
          {error ? <p className={styles.errorText}>{error}</p> : null}
          <Button
            onClick={handleExecuteCallback}
            white
            disabled={isLoading || isCallbackExecuting || code.length !== 6}
          >
            {isLoading || isCallbackExecuting ? (
              <LoadingImageElement />
            ) : (
              getTextByKey("check-mfa-code")
            )}
          </Button>
          <Button
            transparent
            buttonClass={`${styles.buttonLink} ${
              !isResendEnabled ? `${styles.disabled}` : ""
            } `}
            disabled={!isResendEnabled || isLoading}
            onClick={handleResendMfaCode}
          >
            {getTextByKey("resend-mfa-code")}{" "}
            {resendTimeLeft > 0 ? `(${resendTimeLeft})` : ""}
          </Button>
        </>
      ) : (
        <>
          <h2>
            {getTextByKey(
              isSubUsersManagement
                ? `
              send-email-${
                enableMfa ? "enable" : "disable"
              }-mfa-title-sub-users`
                : `send-email-${enableMfa ? "enable" : "disable"}-mfa-title`
            )}
          </h2>
          <p>
            {getTextByKey(
              isSubUsersManagement
                ? `send-email-${
                    enableMfa ? "enable" : "disable"
                  }mfa-description-sub-users`
                : `send-email-${
                    enableMfa ? "enable" : "disable"
                  }mfa-description`
            )}
          </p>
          <Button onClick={handleResendMfaCode} white disabled={isLoading}>
            {isLoading ? (
              <LoadingImageElement />
            ) : (
              getTextByKey("send-email-mfa")
            )}
          </Button>
        </>
      )}
      {isMailSent || skipFirstStep ? (
        <p>
          {dayjs(timeLeft).format("mm[m]:ss[s]")}{" "}
          {getTextByKey("time-left-validation")}
        </p>
      ) : null}
    </div>
  );
};

export default ManageMfaModal;
